import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { useAuth } from '../components/Auth/AuthContext';
import { supabase } from '../lib/supabaseClient';
import messagingService from '../services/messaging';

const MessagingContext = createContext();

export const useMessaging = () => {
  const context = useContext(MessagingContext);
  if (context === undefined) {
    throw new Error('useMessaging must be used within a MessagingProvider');
  }
  return context;
};

export const MessagingProvider = ({ children }) => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Refs for subscriptions
  const conversationSubscription = useRef(null);

  // Load conversations
  const loadConversations = useCallback(async () => {
    if (!user) return;

    try {
      const result = await messagingService.getConversations();

      if (result.success && result.data) {
        // Process conversations to get participant info
        const processedConversations = await Promise.all(
          result.data.map(async (item) => {
            // Get other participants
            const { data: participants } = await messagingService.getConversationParticipants(
              item.conversation_id
            );

            const otherParticipants = participants?.filter((p) => p.user_id !== user.id) || [];

            // Enrich copilot conversations with event-aware display name
            let copilotDisplayName = null;
            const metadata = item.conversations?.metadata;
            if (metadata && metadata.agent === 'copilot' && metadata.eventId) {
              try {
                const { data: event } = await supabase
                  .from('events')
                  .select('id, title')
                  .eq('id', metadata.eventId)
                  .maybeSingle();
                if (event?.title) {
                  copilotDisplayName = `Co‑Pilot · ${event.title}`;
                }
              } catch (e) {
                // non-fatal
              }
            }

            return {
              ...item.conversations,
              conversation_id: item.conversation_id,
              participants: otherParticipants.map((p) => {
                if (copilotDisplayName && p?.profiles) {
                  return { ...p, profiles: { ...p.profiles, display_name: copilotDisplayName } };
                }
                return p;
              }),
              unread_count: item.unread_count || 0,
              copilot_display_name: copilotDisplayName,
            };
          })
        );

        setConversations(processedConversations);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  }, [user]);

  // Load blocked users
  const loadBlockedUsers = useCallback(async () => {
    if (!user) return;

    try {
      const result = await messagingService.getBlockedUsers();

      if (result.success && result.data) {
        setBlockedUsers(result.data.map((b) => b.blocked_id));
      }
    } catch (error) {
      console.error('Error loading blocked users:', error);
    }
  }, [user]);

  // Toggle block user
  const toggleBlockUser = useCallback(
    async (userId, isBlocked) => {
      if (!user) return;

      try {
        const result = isBlocked
          ? await messagingService.unblockUser(userId)
          : await messagingService.blockUser(userId);

        if (result.success) {
          setBlockedUsers((prev) =>
            isBlocked ? prev.filter((id) => id !== userId) : [...prev, userId]
          );
        }
      } catch (error) {
        console.error('Error toggling block:', error);
      }
    },
    [user]
  );

  // Update conversation unread count
  const markConversationAsRead = useCallback((conversationId) => {
    setConversations((prev) =>
      prev.map((conv) =>
        conv.conversation_id === conversationId ? { ...conv, unread_count: 0 } : conv
      )
    );
  }, []);

  // Subscribe to conversation changes
  useEffect(() => {
    if (!user || !initialized) return;

    // Subscribe to conversation participant changes to detect new conversations
    conversationSubscription.current = supabase
      .channel('messaging-context-conversations')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversation_participants',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          // Reload conversations when user joins/leaves conversations
          loadConversations();
        }
      )
      .subscribe();

    return () => {
      conversationSubscription.current?.unsubscribe();
    };
  }, [user, initialized, loadConversations]);

  // Initial load
  useEffect(() => {
    const init = async () => {
      if (!user || initialized) return;

      // Verify we have a valid session before trying to load data
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session) {
          console.warn('MessagingContext: No active session yet, skipping initialization');
          return;
        }

        setLoading(true);
        await Promise.all([loadConversations(), loadBlockedUsers()]);
        setLoading(false);
        setInitialized(true);
      } catch (error) {
        console.error('MessagingContext: Error checking session:', error);
        setLoading(false);
      }
    };

    init();
  }, [user, initialized, loadConversations, loadBlockedUsers]);

  // Reset on logout
  useEffect(() => {
    if (!user) {
      setConversations([]);
      setBlockedUsers([]);
      setInitialized(false);
      setLoading(true);
    }
  }, [user]);

  const value = {
    conversations,
    blockedUsers,
    loading,
    loadConversations,
    toggleBlockUser,
    markConversationAsRead,
  };

  return <MessagingContext.Provider value={value}>{children}</MessagingContext.Provider>;
};
